import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  ToggleButton,
  ToggleButtonGroup,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import AppLayout from '../layout/AppLayout';
import { invoiceService } from '../../services/api';
import {
  INK,
  MUTED,
  RULE,
  ChartTooltip,
  EmptyState,
  FigureStrip,
  LoadingState,
  Section,
  axisProps,
  formatCount,
  formatINR,
  formatPercent,
  prefersReducedMotion
} from '../analytics/analyticsUi';

const UNPAID_COLOR = '#E8B04B';
const STATUS_COLORS = { paid: '#1BAA52', due: '#9DB4D3', overdue: '#C2410C', failed: '#8B1E1E' };
const TYPE_COLORS = { monthly: '#128C7E', subscription: '#7C6FD6' };
const PAID_COLOR = '#1BAA52';
const AGEING_COLORS = { current: '#9DB4D3', '1-15': '#E8B04B', '16-30': '#E08A3C', '31-60': '#C2410C', '60+': '#8B1E1E' };
const PAYMENT_METHODS = {
  manual: 'Bank transfer / manual',
  upi: 'UPI',
  net_banking: 'Net banking',
  credit_card: 'Credit card',
  debit_card: 'Debit card',
  wallet: 'Wallet',
  offline: 'Cash / cheque'
};

const rupees = (value) => formatINR(value || 0);
const compactINR = (value) => {
  if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (Math.abs(value) >= 1000) return `₹${Math.round(value / 1000)}k`;
  return `₹${Math.round(value)}`;
};
// Small shares still show as more than zero
const share = (value) => (value > 0 && value < 0.1 ? 'under 0.1%' : formatPercent(value));
const formatDay = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–');
const shortMonth = (key) => new Date(`${key}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

// Last 13 calendar months, newest first
const monthOptions = () => {
  const now = new Date();
  return Array.from({ length: 13 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
    };
  });
};

const Amount = ({ value, muted, bold }) => (
  <Typography component="span" variant="body2" sx={{ color: muted ? MUTED : INK, fontWeight: bold ? 600 : 400, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
    {value ? rupees(value) : '–'}
  </Typography>
);

// ---------------------------------------------------------------------------
// Forecast by invoice month: last month, this month and the next two
// ---------------------------------------------------------------------------
const Forecast = ({ forecast, estimateFrom }) => {
  const anyEstimate = forecast.some((f) => f.messageChargesEstimated);
  return (
    <Section
      title="Expected by invoice month"
      description="Each month's invoices, whenever they are paid: what is invoiced, paid and awaiting payment, and what is still to be invoiced. Message charges for a month are invoiced after it ends."
    >
      <TableContainer>
        <Table size="small" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE, verticalAlign: 'bottom' } }}>
              <TableCell sx={{ pl: 0 }}>Invoice month</TableCell>
              <TableCell align="right">Invoiced</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Awaiting payment</TableCell>
              <TableCell align="right">Subscription fees to invoice</TableCell>
              <TableCell align="right">Message charges to invoice</TableCell>
              <TableCell align="right">Expected total</TableCell>
              <TableCell align="right" sx={{ pr: 0 }}>Still to collect</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forecast.map((f) => (
              <TableRow key={f.key} sx={{ '& td': { borderColor: RULE, verticalAlign: 'top', py: 1.5 } }}>
                <TableCell sx={{ pl: 0 }}>
                  <Typography variant="body2" sx={{ color: INK, fontWeight: 600, whiteSpace: 'nowrap' }}>{f.label}</Typography>
                  {f.current && <Typography variant="caption" sx={{ color: MUTED }}>This month</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Amount value={f.invoiced} />
                  {f.invoicedCount > 0 && <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{formatCount(f.invoicedCount)} invoices</Typography>}
                </TableCell>
                <TableCell align="right"><Amount value={f.paid} /></TableCell>
                <TableCell align="right"><Amount value={f.awaiting} /></TableCell>
                <TableCell align="right">
                  <Amount value={f.subscriptionFees} />
                  {f.subscriptionCount > 0 && <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{formatCount(f.subscriptionCount)} plans</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Amount value={f.messageCharges} muted={f.messageChargesEstimated} />
                  {f.messageChargesEstimated && <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>Estimate</Typography>}
                </TableCell>
                <TableCell align="right"><Amount value={f.expected} bold /></TableCell>
                <TableCell align="right" sx={{ pr: 0 }}><Amount value={f.toCollect} bold /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5, maxWidth: '90ch' }}>
        All amounts include GST.{' '}
        {anyEstimate && estimateFrom.length > 0
          ? `Message charges not invoiced yet are estimated as the average of ${estimateFrom.map(shortMonth).join(', ')}. `
          : ''}
        Subscription fees come from active subscriptions that have no invoice for the month yet.
      </Typography>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Billed, paid and unpaid for each invoice month
// ---------------------------------------------------------------------------
const Trend = ({ trend }) => {
  const [view, setView] = useState('status');
  const data = trend.map((t) => ({
    period: shortMonth(t.key),
    paid: t.paid,
    unpaid: t.unpaid,
    messages: t.billed_monthly,
    subscriptions: t.billed_subscription
  }));
  const empty = trend.every((t) => !t.billed);
  const bars = view === 'status'
    ? [['paid', 'Paid', PAID_COLOR], ['unpaid', 'Awaiting payment', UNPAID_COLOR]]
    : [['messages', 'Message charges', TYPE_COLORS.monthly], ['subscriptions', 'Subscription fees', TYPE_COLORS.subscription]];
  return (
    <Section
      title="Invoice months"
      description={view === 'status'
        ? "Each bar is one month's invoices: the paid part and the part still awaiting payment."
        : "Each bar is one month's invoices, split into message charges and subscription fees."}
      action={(
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)} aria-label="Chart view">
          <ToggleButton value="status" sx={{ textTransform: 'none', px: 1.5 }}>Paid and unpaid</ToggleButton>
          <ToggleButton value="type" sx={{ textTransform: 'none', px: 1.5 }}>By type</ToggleButton>
        </ToggleButtonGroup>
      )}
    >
      {empty ? (
        <Typography variant="body2" sx={{ color: MUTED }}>No invoices in these twelve months.</Typography>
      ) : (
        <Box sx={{ height: 280 }} role="img" aria-label={`Stacked bar chart of the last twelve invoice months, ${view === 'status' ? 'paid and unpaid' : 'by type'}`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={RULE} vertical={false} />
              <XAxis dataKey="period" {...axisProps} />
              <YAxis {...axisProps} axisLine={false} width={56} tickFormatter={compactINR} />
              <Tooltip content={<ChartTooltip formatter={rupees} />} cursor={{ fill: 'rgba(31, 42, 61, 0.04)' }} />
              <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 13, color: MUTED }} />
              {bars.map(([key, name, color], index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={name}
                  stackId="month"
                  fill={color}
                  maxBarSize={36}
                  radius={index === bars.length - 1 ? [3, 3, 0, 0] : 0}
                  isAnimationActive={!prefersReducedMotion()}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Status of the month's invoices
// ---------------------------------------------------------------------------
const StatusChart = ({ breakdown, month }) => {
  const total = breakdown.reduce((sum, b) => sum + b.total, 0);
  const slices = breakdown.filter((b) => b.total > 0);
  return (
    <Section title={`${month} invoices by status`} sx={{ height: 'calc(100% - 24px)' }}>
      {!total ? (
        <Typography variant="body2" sx={{ color: MUTED }}>No invoices for this month.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3 }}>
          <Box sx={{ width: 180, height: 180, position: 'relative', flexShrink: 0 }} role="img" aria-label={`Donut chart: ${slices.map((b) => `${b.label} ${formatPercent((b.total / total) * 100)}`).join(', ')}`}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="total"
                  nameKey="label"
                  innerRadius={58}
                  outerRadius={86}
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                  minAngle={3}
                  isAnimationActive={!prefersReducedMotion()}
                >
                  {slices.map((b) => <Cell key={b.key} fill={STATUS_COLORS[b.key]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.1rem', fontVariantNumeric: 'tabular-nums' }}>
                {share(((breakdown.find((b) => b.key === 'paid')?.total || 0) / total) * 100)}
              </Typography>
              <Typography variant="caption" sx={{ color: MUTED }}>paid</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            {breakdown.map((b) => (
              <Box key={b.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, borderTop: `1px solid ${RULE}` }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: STATUS_COLORS[b.key], flexShrink: 0 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: INK }}>{b.label}</Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>{formatCount(b.count)} {b.count === 1 ? 'invoice' : 'invoices'}</Typography>
                </Box>
                <Amount value={b.total} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// How quickly a month's invoices get paid, against the month before
// ---------------------------------------------------------------------------
const CollectionCurve = ({ curve, month }) => {
  const { current, previous, previousKey } = curve;
  const length = Math.max(current?.points.length || 0, previous?.points.length || 0);
  const data = Array.from({ length }, (_, day) => ({
    day,
    current: current?.points[day]?.percent,
    previous: previous?.points[day]?.percent
  }));
  const previousLabel = new Date(`${previousKey}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const average = (c) => (c?.averageDaysToPay === null || c?.averageDaysToPay === undefined ? null : `${c.averageDaysToPay.toFixed(1)} days`);
  return (
    <Section
      title="How fast invoices get paid"
      description="Share of the month's invoiced amount paid within a number of days of the invoice date."
      sx={{ height: 'calc(100% - 24px)' }}
    >
      {!current && !previous ? (
        <Typography variant="body2" sx={{ color: MUTED }}>No invoices for {month} or the month before.</Typography>
      ) : (
        <>
          <Box sx={{ height: 200 }} role="img" aria-label={`Line chart of the share of ${month} invoices paid by days after issue, compared with ${previousLabel}`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={RULE} vertical={false} />
                <XAxis dataKey="day" {...axisProps} tickFormatter={(d) => `${d}d`} minTickGap={16} />
                <YAxis {...axisProps} axisLine={false} width={44} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  content={(props) => <ChartTooltip {...props} label={`Within ${props.label} days of the invoice`} formatter={(v) => `${Number(v).toFixed(1)}%`} />}
                />
                <Legend iconType="plainline" wrapperStyle={{ fontSize: 13, color: MUTED }} />
                {current && <Line type="stepAfter" dataKey="current" name={month} stroke={PAID_COLOR} strokeWidth={2.5} dot={false} connectNulls={false} isAnimationActive={!prefersReducedMotion()} />}
                {previous && <Line type="stepAfter" dataKey="previous" name={previousLabel} stroke="#9DB4D3" strokeWidth={2} strokeDasharray="4 3" dot={false} isAnimationActive={!prefersReducedMotion()} />}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1 }}>
            Average time to pay: {month} {average(current) || 'no payments yet'}
            {previous ? `; ${previousLabel} ${average(previous) || 'no payments'}` : ''}.
          </Typography>
        </>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Outstanding by age
// ---------------------------------------------------------------------------
const Ageing = ({ ageing, outstanding, label }) => (
  <Section title={`Unpaid ${label} invoices by age`} description="By how far past their due date they are." sx={{ height: 'calc(100% - 24px)' }}>
    {!outstanding.total ? (
      <Typography variant="body2" sx={{ color: MUTED }}>Nothing awaiting payment.</Typography>
    ) : (
      <>
        <Box sx={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', bgcolor: RULE, mb: 2 }} aria-hidden="true">
          {ageing.filter((a) => a.total > 0).map((a) => (
            <Box key={a.key} sx={{ width: `${(a.total / outstanding.total) * 100}%`, bgcolor: AGEING_COLORS[a.key] }} />
          ))}
        </Box>
        {ageing.map((a) => (
          <Box key={a.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderTop: `1px solid ${RULE}` }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: AGEING_COLORS[a.key], flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: INK, flexGrow: 1 }}>{a.label}</Typography>
            <Typography variant="caption" sx={{ color: MUTED, minWidth: 72, textAlign: 'right' }}>
              {a.count ? `${formatCount(a.count)} ${a.count === 1 ? 'invoice' : 'invoices'}` : ''}
            </Typography>
            <Box sx={{ minWidth: 110, textAlign: 'right' }}><Amount value={a.total} /></Box>
          </Box>
        ))}
      </>
    )}
  </Section>
);

// ---------------------------------------------------------------------------
// Paid for the month, by type and method
// ---------------------------------------------------------------------------
const PaidBreakdown = ({ data }) => {
  const methods = Object.entries(data.paid.methods || {}).sort((a, b) => b[1] - a[1]);
  const rows = [
    ['Message charges', data.paid.monthly, data.billed.monthly],
    ['Subscription fees', data.paid.subscription, data.billed.subscription]
  ];
  return (
    <Section title={`Paid for ${data.month.label}`} description="Payments against this month's invoices, whenever they were received." sx={{ height: 'calc(100% - 24px)' }}>
      {rows.map(([label, value, billed]) => (
        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderTop: `1px solid ${RULE}` }}>
          <Typography variant="body2" sx={{ color: INK }}>{label}</Typography>
          <Typography variant="body2" sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ color: INK }}>{rupees(value)}</Box> of {rupees(billed)}
          </Typography>
        </Box>
      ))}
      {methods.length > 0 && (
        <>
          <Typography variant="body2" sx={{ color: MUTED, fontWeight: 600, mt: 2, mb: 0.5 }}>By payment method</Typography>
          {methods.map(([method, value]) => (
            <Box key={method} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderTop: `1px solid ${RULE}` }}>
              <Typography variant="body2" sx={{ color: INK }}>{PAYMENT_METHODS[method] || method}</Typography>
              <Amount value={value} />
            </Box>
          ))}
        </>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const PaymentsDashboard = () => {
  const months = useMemo(() => monthOptions(), []);
  const [month, setMonth] = useState(months[0].key);
  const [kind, setKind] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await invoiceService.paymentsSummary(month, kind === 'all' ? undefined : kind);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'The payment figures could not be loaded. Try again.');
    } finally {
      setLoading(false);
    }
  }, [month, kind]);

  useEffect(() => { load(); }, [load]);

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
              Payments
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
              Each month's invoices and how much of them is paid, with what to expect next.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="pay-month">Month</InputLabel>
              <Select labelId="pay-month" label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
                {months.map((m, i) => <MenuItem key={m.key} value={m.key}>{m.label}{i === 0 ? ' (this month)' : ''}</MenuItem>)}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={kind}
              onChange={(e, value) => value && setKind(value)}
              aria-label="Invoice type"
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>All</ToggleButton>
              <ToggleButton value="monthly" sx={{ textTransform: 'none', px: 1.5 }}>Message charges</ToggleButton>
              <ToggleButton value="subscription" sx={{ textTransform: 'none', px: 1.5 }}>Subscriptions</ToggleButton>
            </ToggleButtonGroup>
            <Button component={RouterLink} to="/admin/invoices" variant="outlined" sx={{ textTransform: 'none' }}>Invoices</Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>{error}</Alert>}

        {loading && !data ? <LoadingState /> : data && (
          <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }} aria-busy={loading}>
            <FigureStrip
              figures={[
                {
                  label: `Billed for ${data.month.label}`,
                  value: rupees(data.billed.total),
                  hint: data.billed.count
                    ? `${formatCount(data.billed.count)} invoices: messages ${rupees(data.billed.monthly)}, subscriptions ${rupees(data.billed.subscription)}`
                    : (data.month.current ? 'Message charges are invoiced after the month ends' : 'No invoices for this month')
                },
                {
                  label: 'Paid',
                  value: rupees(data.paid.total),
                  meter: data.collectionRate ?? undefined,
                  hint: data.collectionRate === null
                    ? 'Nothing billed yet'
                    : `${formatCount(data.paid.count)} of ${formatCount(data.billed.count)} invoices, ${share(data.collectionRate)} of the amount`
                },
                {
                  label: 'Awaiting payment',
                  value: rupees(data.awaiting.total),
                  hint: `${formatCount(data.awaiting.count)} ${data.awaiting.count === 1 ? 'invoice' : 'invoices'}`
                },
                {
                  label: 'Overdue',
                  value: rupees(data.overdue.total),
                  hint: data.overdue.count ? `${formatCount(data.overdue.count)} past their due date` : 'None past due'
                }
              ]}
            />

            {data.drafts.count > 0 && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 3 }} action={<Button component={RouterLink} to="/admin/invoices" color="inherit" size="small" sx={{ textTransform: 'none' }}>Review</Button>}>
                {formatCount(data.drafts.count)} draft {data.drafts.count === 1 ? 'invoice' : 'invoices'} ({rupees(data.drafts.total)}) {data.drafts.count === 1 ? "isn't" : "aren't"} counted until approved.
              </Alert>
            )}

            <Forecast forecast={data.forecast} estimateFrom={data.messageEstimateFrom} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}>
              <StatusChart breakdown={data.statusBreakdown} month={data.month.label} />
              <CollectionCurve curve={data.collectionCurve} month={data.month.label} />
            </Box>
            <Trend trend={data.trend} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}>
              <Ageing ageing={data.ageing} outstanding={data.awaiting} label={data.month.label} />
              <PaidBreakdown data={data} />
            </Box>

            <Section title={`Customers who still owe for ${data.month.label}`} description="The largest unpaid amounts on this month's invoices, including failed payments.">
              {!data.topCustomers.length ? (
                <Typography variant="body2" sx={{ color: MUTED }}>Nothing awaiting payment.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE } }}>
                        <TableCell sx={{ pl: 0 }}>Customer</TableCell>
                        <TableCell align="right">Invoices</TableCell>
                        <TableCell align="right">Oldest due</TableCell>
                        <TableCell align="right">Overdue</TableCell>
                        <TableCell align="right" sx={{ pr: 0 }}>Outstanding</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.topCustomers.map((c) => (
                        <TableRow key={`${c.customer}-${c.oldestDue}`} sx={{ '& td': { borderColor: RULE } }}>
                          <TableCell sx={{ pl: 0, maxWidth: 320 }}>
                            <Typography variant="body2" sx={{ color: INK }} noWrap title={c.customer}>{c.customer}</Typography>
                          </TableCell>
                          <TableCell align="right">{formatCount(c.count)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: new Date(c.oldestDue) < new Date() ? '#C2410C' : INK }}>{formatDay(c.oldestDue)}</TableCell>
                          <TableCell align="right"><Amount value={c.overdue} /></TableCell>
                          <TableCell align="right" sx={{ pr: 0, minWidth: 180 }}>
                            <Amount value={c.total} bold />
                            <Box sx={{ height: 4, borderRadius: 2, bgcolor: RULE, mt: 0.5, ml: 'auto', width: '100%', maxWidth: 160 }} aria-hidden="true">
                              <Box sx={{ height: '100%', borderRadius: 2, bgcolor: c.overdue ? '#C2410C' : UNPAID_COLOR, width: `${(c.total / data.topCustomers[0].total) * 100}%`, ml: 'auto' }} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>

            <Section
              title={`Payments for ${data.month.label} invoices`}
              description="Invoices for this month that are paid, whenever the payment was received."
            >
              {!data.monthPayments.length ? (
                <EmptyState title={`No ${data.month.label} invoices are paid yet`}>Mark invoices as paid from the Invoices page, one at a time or several at once.</EmptyState>
              ) : (
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE } }}>
                        <TableCell sx={{ pl: 0 }}>Paid on</TableCell>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell align="right" sx={{ pr: 0 }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.monthPayments.map((p) => (
                        <TableRow key={p.id} sx={{ '& td': { borderColor: RULE } }}>
                          <TableCell sx={{ pl: 0, whiteSpace: 'nowrap' }}>{formatDay(p.paidDate)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <MuiTooltip title={p.kind === 'monthly' ? 'Message charges' : 'Subscription'}>
                              <span>{p.invoiceNumber}</span>
                            </MuiTooltip>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}><Typography variant="body2" noWrap title={p.customer}>{p.customer}</Typography></TableCell>
                          <TableCell>{PAYMENT_METHODS[p.paymentMethod] || p.paymentMethod || '–'}</TableCell>
                          <TableCell align="right" sx={{ pr: 0 }}><Amount value={p.total} bold /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          </Box>
        )}
      </Container>
    </AppLayout>
  );
};

export default PaymentsDashboard;
